const BASE = process.env.REACT_APP_API_URL || '/api';

function getToken() { return localStorage.getItem('fuhrpark_token') || ''; }

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(BASE + path, { headers, ...options });
  if (res.status === 401) {
    localStorage.removeItem('fuhrpark_token');
    window.location.reload();
    return;
  }
  if (!res.ok) { const err = await res.json().catch(() => ({ error: 'Netzwerkfehler' })); throw new Error(err.error || 'Fehler'); }
  return res.json();
}

async function upload(path, formData) {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(BASE + path, { method: 'POST', body: formData, headers });
  if (res.status === 401) {
    localStorage.removeItem('fuhrpark_token');
    window.location.reload();
    return;
  }
  if (!res.ok) { const err = await res.json().catch(() => ({ error: 'Upload fehlgeschlagen' })); throw new Error(err.error || 'Fehler'); }
  return res.json();
}

export const api = {
  getDashboard: () => request('/dashboard'),

  // Auth
  login: (email, password) => request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: (token) => request('/auth/logout', { method: 'POST', body: JSON.stringify({ token }) }),
  getMe: () => request('/auth/me'),

  // Users (Admin)
  getUsers: () => request('/users'),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),

  // Einstellungen
  getEinstellungen: () => request('/einstellungen'),
  updateEinstellungen: (data) => request('/einstellungen', { method: 'PUT', body: JSON.stringify(data) }),

  // Kunden
  getKunden: () => request('/kunden'),
  getKunde: (id) => request(`/kunden/${id}`),
  createKunde: (data) => request('/kunden', { method: 'POST', body: JSON.stringify(data) }),
  updateKunde: (id, data) => request(`/kunden/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteKunde: (id) => request(`/kunden/${id}`, { method: 'DELETE' }),

  // Fahrzeuge
  getFahrzeuge: () => request('/fahrzeuge'),
  getFahrzeugeByKunde: (kundenId) => request(`/kunden/${kundenId}/fahrzeuge`),
  getFahrzeug: (id) => request(`/fahrzeuge/${id}`),
  createFahrzeug: (data) => request('/fahrzeuge', { method: 'POST', body: JSON.stringify(data) }),
  updateFahrzeug: (id, data) => request(`/fahrzeuge/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFahrzeug: (id) => request(`/fahrzeuge/${id}`, { method: 'DELETE' }),
  erledigenFaelligkeit: (id, data) => request(`/fahrzeuge/${id}/erledigen`, { method: 'POST', body: JSON.stringify(data) }),

  // Kontaktbuch (neu)
  getKontaktbuch: () => request('/kontaktbuch'),
  getKontaktbuchByTyp: (typ) => request('/kontaktbuch?typ=' + encodeURIComponent(typ)),
  searchKontaktbuch: (q) => request(`/kontaktbuch/search?q=${encodeURIComponent(q)}`),
  createKontakt: (data) => request('/kontaktbuch', { method: 'POST', body: JSON.stringify(data) }),
  updateKontakt: (id, data) => request(`/kontaktbuch/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteKontakt: (id) => request(`/kontaktbuch/${id}`, { method: 'DELETE' }),
  getAnsprechpartner: (kontaktId) => request(`/kontaktbuch/${kontaktId}/ansprechpartner`),
  createAnsprechpartner: (kontaktId, data) => request(`/kontaktbuch/${kontaktId}/ansprechpartner`, { method: 'POST', body: JSON.stringify(data) }),
  updateAnsprechpartner: (id, data) => request(`/ansprechpartner/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAnsprechpartner: (id) => request(`/ansprechpartner/${id}`, { method: 'DELETE' }),
  unlinkAnsprechpartner: (kontaktId, apId) => request(`/kontaktbuch/${kontaktId}/ansprechpartner/${apId}`, { method: 'DELETE' }),
  importKontaktePreview: (file) => {
    const fd = new FormData(); fd.append('datei', file);
    return upload('/kontaktbuch/import/preview', fd);
  },
  importKontakteCommit: (contacts) => request('/kontaktbuch/import/commit', { method: 'POST', body: JSON.stringify({ contacts }) }),
  getFahrzeugAnsprechpartner: (id) => request(`/fahrzeuge/${id}/ansprechpartner`),
  updateFahrzeugAnsprechpartner: (id, assignments) => request(`/fahrzeuge/${id}/ansprechpartner`, { method: 'PUT', body: JSON.stringify({ assignments }) }),

  // Personen (zentrale Datenbank)
  getPersonen: (q) => request(`/personen${q ? '?q=' + encodeURIComponent(q) : ''}`),
  createPerson: (data) => request('/personen', { method: 'POST', body: JSON.stringify(data) }),

  // Fahrer
  getFahrer: (fahrzeugId) => request(`/fahrzeuge/${fahrzeugId}/fahrer`),
  addFahrer: (fahrzeugId, data) => request(`/fahrzeuge/${fahrzeugId}/fahrer`, { method: 'POST', body: JSON.stringify(data) }),
  removeFahrer: (fahrzeugId, apId) => request(`/fahrzeuge/${fahrzeugId}/fahrer/${apId}`, { method: 'DELETE' }),

  // Betriebe (Rückwärtskompatibilität → kontaktbuch)
  getBetriebe: () => request('/kontaktbuch'),
  searchBetriebe: (q) => request(`/kontaktbuch/search?q=${encodeURIComponent(q)}`),

  // Radsätze
  getAllRadsaetze: () => request('/radsaetze'),
  getRadsaetze: (fahrzeugId) => request(`/fahrzeuge/${fahrzeugId}/radsaetze`),
  createRadsatz: (fahrzeugId, data) => request(`/fahrzeuge/${fahrzeugId}/radsaetze`, { method: 'POST', body: JSON.stringify(data) }),
  updateRadsatz: (id, data) => request(`/radsaetze/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRadsatz: (id) => request(`/radsaetze/${id}`, { method: 'DELETE' }),

  // KM-Verlauf
  getKmVerlauf: (fahrzeugId) => request(`/fahrzeuge/${fahrzeugId}/km`),
  addKmEintrag: (fahrzeugId, data) => request(`/fahrzeuge/${fahrzeugId}/km`, { method: 'POST', body: JSON.stringify(data) }),
  deleteKmEintrag: (id) => request(`/km/${id}`, { method: 'DELETE' }),

  // Ereignisse
  getEreignisse: (fahrzeugId) => request(`/fahrzeuge/${fahrzeugId}/ereignisse`),
  createEreignis: (fahrzeugId, data) => request(`/fahrzeuge/${fahrzeugId}/ereignisse`, { method: 'POST', body: JSON.stringify(data) }),
  updateEreignis: (id, data) => request(`/ereignisse/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEreignis: (id) => request(`/ereignisse/${id}`, { method: 'DELETE' }),

  // Dokumente (Fahrzeug)
  getDokumente: (fahrzeugId) => request(`/fahrzeuge/${fahrzeugId}/dokumente`),
  uploadDokument: (fahrzeugId, formData) => upload(`/fahrzeuge/${fahrzeugId}/dokumente`, formData),
  deleteDokument: (id) => request(`/dokumente/${id}`, { method: 'DELETE' }),
  getDokumentUrl: (id, download = false) => `${BASE}/dokumente/${id}/download${download ? '?dl=1' : ''}`,

  // Dokumente (Kontakt)
  getKontaktDokumente: (kontaktId) => request(`/kontaktbuch/${kontaktId}/dokumente`),
  uploadKontaktDokument: (kontaktId, formData) => upload(`/kontaktbuch/${kontaktId}/dokumente`, formData),
  deleteKontaktDokument: (id) => request(`/kontakt_dokumente/${id}`, { method: 'DELETE' }),
  getKontaktDokumentUrl: (id, download = false) => `${BASE}/kontakt_dokumente/${id}/download${download ? '?dl=1' : ''}`,

  // Rollen
  getRollen: () => request('/rollen'),
  createRolle: (data) => request('/rollen', { method: 'POST', body: JSON.stringify(data) }),
  updateRolle: (id, data) => request(`/rollen/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRolle: (id) => request(`/rollen/${id}`, { method: 'DELETE' }),

};
